import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  HoverCard,
  IconButton,
  Input,
  Menu,
  Portal,
  Stack,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { LuCheck, LuInfo, LuListFilter, LuListStart, LuPlus } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { getUnionDemands, createDemand } from '../api'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import type { CreateDemandPayload, Demand } from '../types'
import {
  getUnionDemandStatusTone as statusTone,
  isDemandFinished,
  type UnionDemandStatusTone,
} from '../unionDemandStatus'
import { CreateDemandDrawer } from '../components/CreateDemandDrawer'

type ListMode = 'open' | 'archive' | 'all'

type StatusTone = UnionDemandStatusTone

const STATUS_FILTER_OPTIONS = [
  'Handlung erforderlich',
  'Laufend',
  'Teilweise vermittelt',
  'Vermittelt',
  'Nicht vermittelbar',
  'Nicht vermittelbarer Rest',
]

const VISIBILITY_FILTER_OPTIONS = [
  { value: 'selected', label: 'Auswahl' },
  { value: 'all_users', label: 'Alle SIB' },
  { value: 'draft', label: 'Entwurf' },
]

const SORT_OPTIONS = [
  { value: 'replyAsc', label: 'Rückmeldung bis aufsteigend' },
  { value: 'replyDesc', label: 'Rückmeldung bis absteigend' },
  { value: 'status', label: 'Status' },
  { value: 'bankAZ', label: 'Bank mit Bedarf A–Z' },
  { value: 'vxx', label: 'Vxx' },
  { value: 'interestDesc', label: 'Anzahl Interessenten absteigend' },
  { value: 'createdDesc', label: 'Zuletzt erfasst' },
] as const

type SortKey = (typeof SORT_OPTIONS)[number]['value']

const STATUS_LEGEND: Array<[StatusTone['colorPalette'], string, string]> = [
  [
    'yellow',
    'Handlung erforderlich',
    'Bedarf wurde erfasst, aber noch nicht ausgespielt, oder Rückmeldungen müssen ausgewertet werden.',
  ],
  ['blue', 'Laufend', 'Bedarf ist ausgespielt und wartet auf Rückmeldungen.'],
  ['green', 'Vermittelt', 'Bedarf wurde vermittelt und abgeschlossen.'],
  ['red', 'Nicht vermittelbar', 'Bedarf konnte aktuell nicht gedeckt werden.'],
]

const stickyColStyles = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 2,
  bg: 'bg',
  borderRightWidth: '1px',
  borderRightColor: 'border',
}

const stickyHeaderStyles = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 4,
  bg: 'bg',
  borderRightWidth: '1px',
  borderRightColor: 'border',
}

const tableCellProps = {
  px: 4,
  py: 4,
  verticalAlign: 'middle',
}

function toDateOnly(value?: string) {
  return value ? value.split('T')[0] : undefined
}

function formatDate(value?: string) {
  const dateOnly = toDateOnly(value)
  if (!dateOnly) return '—'

  const [year, month, day] = dateOnly.split('-')
  return `${day}.${month}.${year}`
}

function formatPeriod(start?: string, end?: string) {
  if (!start && !end) return '—'
  return `${formatDate(start)}–${formatDate(end)}`
}

function startOfToday() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

function statusRank(label: string) {
  return STATUS_FILTER_OPTIONS.indexOf(label)
}

function visibilityValue(demand: Demand) {
  if (!demand.published) return 'draft'
  return demand.visibilityMode === 'ALL_USERS' ? 'all_users' : 'selected'
}

function visibilityTone(demand: Demand): StatusTone {
  const visibility = visibilityValue(demand)

  if (visibility === 'draft') return { colorPalette: 'gray', label: 'Entwurf' }
  if (visibility === 'all_users') return { colorPalette: 'green', label: 'Alle SIB' }
  return { colorPalette: 'blue', label: 'Auswahl' }
}

function remainingDaysLabel(value?: string) {
  const dateOnly = toDateOnly(value)
  if (!dateOnly) return null

  const target = new Date(dateOnly)
  target.setHours(0, 0, 0, 0)

  const days = Math.max(0, Math.ceil((target.getTime() - startOfToday().getTime()) / 86_400_000))
  return `${days} ${days === 1 ? 'Tag' : 'Tage'}`
}

function replyUntilSort(demand: Demand) {
  const dateOnly = toDateOnly(demand.expiresAt)
  return dateOnly ? new Date(dateOnly).getTime() : Number.MAX_SAFE_INTEGER
}

function bankName(demand: Demand) {
  return demand.customerName || demand.bankName || demand.serviceRecipient || '—'
}

function servicesText(demand: Demand) {
  const buckets = demand.buckets ?? []

  if (buckets.length === 0) {
    return '—'
  }

  const get = (type: string) =>
    buckets.find(bucket => bucket.serviceType === type)?.requestedUnits ?? 0

  return `${get('TOJ')} TOJ · ${get('CONSULTING')} Std. · ${get('TRAINING')} Tr. · ${get(
    'PILOT'
  )} Pilot`
}

function responsesText(demand: Demand) {
  if (demand.interestsCount === undefined && demand.rejectedCount === undefined) {
    return '—'
  }

  const interest = demand.interestsCount ?? 0
  const rejections = demand.rejectedCount ?? 0

  return `${interest} Interesse · ${rejections} Ablehnung${rejections === 1 ? '' : 'en'}`
}

function InfoHover({
  label,
  maxW = '360px',
  children,
}: {
  label: string
  maxW?: string
  children: ReactNode
}) {
  return (
    <HoverCard.Root positioning={{ placement: 'bottom', gutter: 8 }}>
      <HoverCard.Trigger asChild>
        <IconButton aria-label={label} size="xs" variant="ghost">
          <LuInfo />
        </IconButton>
      </HoverCard.Trigger>

      <Portal>
        <HoverCard.Positioner>
          <HoverCard.Content maxW={maxW} zIndex="popover">
            <Stack gap={2}>{children}</Stack>
          </HoverCard.Content>
        </HoverCard.Positioner>
      </Portal>
    </HoverCard.Root>
  )
}

export function UnionMarketplacePage() {
  const navigate = useNavigate()

  const [demands, setDemands] = useState<Demand[]>([])
  const [showCreateDrawer, setShowCreateDrawer] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [search, setSearch] = useState('')
  const [listMode, setListMode] = useState<ListMode>('open')
  const [statusFilter, setStatusFilter] = useState('all')
  const [visibilityFilter, setVisibilityFilter] = useState('all')
  const [regionFilter, setRegionFilter] = useState('all')
  const [periodFilter, setPeriodFilter] = useState<'all' | 'with' | 'without'>('all')
  const [responseFilter, setResponseFilter] = useState<'all' | 'interest' | 'none' | 'rejection'>(
    'all'
  )
  const [sortKey, setSortKey] = useState<SortKey>('replyAsc')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function loadDemands() {
    try {
      setIsLoading(true)
      setError(null)

      if (listMode === 'all') {
        const [open, archived] = await Promise.all([getUnionDemands(false), getUnionDemands(true)])
        setDemands([...open, ...archived])
      } else {
        const result = await getUnionDemands(listMode === 'archive')
        setDemands(result)
      }
    } catch {
      setError('Could not load marketplace demands.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadDemands()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listMode])

  async function handleCreateDemand(payload: CreateDemandPayload) {
    try {
      setIsCreating(true)

      await createDemand(payload)

      setShowCreateDrawer(false)
      await loadDemands()
    } finally {
      setIsCreating(false)
    }
  }

  const regions = useMemo(() => {
    return [...new Set(demands.map(demand => demand.salesRegion).filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b), 'de')
    ) as string[]
  }, [demands])

  const visibleDemands = useMemo(() => {
    return demands
      .filter(demand => {
        const query = search.toLowerCase().trim()

        const matchesSearch =
          !query ||
          [
            bankName(demand),
            demand.city ?? '',
            demand.postal ?? '',
            demand.salesRegion ?? '',
            statusTone(demand).label,
            servicesText(demand),
          ]
            .join(' ')
            .toLowerCase()
            .includes(query)

        const matchesStatus = statusFilter === 'all' || statusTone(demand).label === statusFilter

        const matchesVisibility =
          visibilityFilter === 'all' || visibilityFilter === visibilityValue(demand)

        const matchesRegion = regionFilter === 'all' || demand.salesRegion === regionFilter

        const hasPeriod = Boolean(demand.periodStart || demand.periodEnd)
        const matchesPeriod =
          periodFilter === 'all' || (periodFilter === 'with' ? hasPeriod : !hasPeriod)

        const interest = demand.interestsCount ?? 0
        const rejections = demand.rejectedCount ?? 0
        const matchesResponses =
          responseFilter === 'all' ||
          (responseFilter === 'interest' && interest > 0) ||
          (responseFilter === 'none' && interest === 0 && rejections === 0) ||
          (responseFilter === 'rejection' && rejections > 0)

        return (
          matchesSearch &&
          matchesStatus &&
          matchesVisibility &&
          matchesRegion &&
          matchesPeriod &&
          matchesResponses
        )
      })
      .sort((a, b) => {
        if (sortKey === 'replyAsc') return replyUntilSort(a) - replyUntilSort(b)
        if (sortKey === 'replyDesc') return replyUntilSort(b) - replyUntilSort(a)
        if (sortKey === 'status')
          return statusRank(statusTone(a).label) - statusRank(statusTone(b).label)
        if (sortKey === 'bankAZ') return bankName(a).localeCompare(bankName(b), 'de')
        if (sortKey === 'vxx')
          return (a.salesRegion ?? '').localeCompare(b.salesRegion ?? '', 'de')
        if (sortKey === 'interestDesc') return (b.interestsCount ?? 0) - (a.interestsCount ?? 0)

        return (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
      })
  }, [
    demands,
    search,
    statusFilter,
    visibilityFilter,
    regionFilter,
    periodFilter,
    responseFilter,
    sortKey,
  ])

  if (isLoading) {
    return <LoadingState message="Marktplatz wird geladen..." />
  }

  if (error) {
    return <ErrorState message={error} onRetry={loadDemands} />
  }

  const hasActiveFilter = [
    statusFilter,
    visibilityFilter,
    regionFilter,
    periodFilter,
    responseFilter,
  ].some(value => value !== 'all')

  return (
    <VStack align="stretch" gap={4} w="full" p={4}>
      {/* The button's negative vertical margin keeps this row at headline
          height, so the headline and the search bar below sit at the same
          heights as on screens without a header button (SIB-874) */}
      <Flex justify="space-between" align="center">
        <Heading size="lg" fontWeight="normal">
          Marktplatz
        </Heading>

        <Button
          size="sm"
          variant="subtle"
          bg="blue.subtle"
          borderWidth="1px"
          color="blue.fg"
          borderColor="blue.muted"
          my="-1"
          onClick={() => setShowCreateDrawer(true)}
        >
          <LuPlus /> Neuen Bedarf erfassen
        </Button>
      </Flex>

      <HStack w="full" gap={3} align="center" flexWrap="wrap">
        <Input
          flex="1"
          minW="240px"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Marktplatz durchsuchen, Bank oder Ort eingeben"
        />

        <HStack gap={1} borderWidth="1px" borderColor="border" borderRadius="lg" p="1">
          {(
            [
              { key: 'open', label: 'Offen' },
              { key: 'all', label: 'Alle' },
              { key: 'archive', label: 'Archiv' },
            ] as const
          ).map(item => (
            <Button
              key={item.key}
              size="sm"
              variant={listMode === item.key ? 'subtle' : 'ghost'}
              colorPalette={listMode === item.key ? 'blue' : 'gray'}
              onClick={() => setListMode(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </HStack>

        <Menu.Root positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <Menu.Trigger asChild>
            <Button variant="subtle" size="sm" flexShrink={0}>
              <HStack gap={2}>
                <LuListStart />
                <Text>Sortierung</Text>
              </HStack>
            </Button>
          </Menu.Trigger>

          <Portal>
            <Menu.Positioner>
              <Menu.Content minW="280px">
                {SORT_OPTIONS.map(option => (
                  <Menu.Item
                    key={option.value}
                    value={`sort:${option.value}`}
                    onClick={() => setSortKey(option.value)}
                  >
                    <HStack justify="space-between" w="full">
                      <Text>{option.label}</Text>
                      {sortKey === option.value && <LuCheck />}
                    </HStack>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>

        <Menu.Root positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <Menu.Trigger asChild>
            <Button
              variant={hasActiveFilter ? 'solid' : 'subtle'}
              colorPalette={hasActiveFilter ? 'teal' : 'gray'}
              size="sm"
              flexShrink={0}
            >
              <HStack gap={2}>
                <LuListFilter />
                <Text>Filter</Text>
              </HStack>
            </Button>
          </Menu.Trigger>

          <Portal>
            <Menu.Positioner>
              <Menu.Content minW="320px" maxH="70vh" overflowY="auto">
                <Box px={3} py={2}>
                  <Text fontSize="xs" color="fg.subtle" fontWeight="semibold">
                    Status
                  </Text>
                </Box>

                <Menu.Item value="filter:status:all" onClick={() => setStatusFilter('all')}>
                  <HStack justify="space-between" w="full">
                    <Text>Alle Status</Text>
                    {statusFilter === 'all' && <LuCheck />}
                  </HStack>
                </Menu.Item>

                {STATUS_FILTER_OPTIONS.map(option => (
                  <Menu.Item
                    key={option}
                    value={`filter:status:${option}`}
                    onClick={() => setStatusFilter(option)}
                  >
                    <HStack justify="space-between" w="full">
                      <Text>{option}</Text>
                      {statusFilter === option && <LuCheck />}
                    </HStack>
                  </Menu.Item>
                ))}

                <Menu.Separator />

                <Box px={3} py={2}>
                  <Text fontSize="xs" color="fg.subtle" fontWeight="semibold">
                    Sichtbarkeit
                  </Text>
                </Box>

                <Menu.Item
                  value="filter:visibility:all"
                  onClick={() => setVisibilityFilter('all')}
                >
                  <HStack justify="space-between" w="full">
                    <Text>Alle Sichtbarkeiten</Text>
                    {visibilityFilter === 'all' && <LuCheck />}
                  </HStack>
                </Menu.Item>

                {VISIBILITY_FILTER_OPTIONS.map(option => (
                  <Menu.Item
                    key={option.value}
                    value={`filter:visibility:${option.value}`}
                    onClick={() => setVisibilityFilter(option.value)}
                  >
                    <HStack justify="space-between" w="full">
                      <Text>{option.label}</Text>
                      {visibilityFilter === option.value && <LuCheck />}
                    </HStack>
                  </Menu.Item>
                ))}

                <Menu.Separator />

                <Box px={3} py={2}>
                  <Text fontSize="xs" color="fg.subtle" fontWeight="semibold">
                    Vxx
                  </Text>
                </Box>

                <Menu.Item value="filter:vxx:all" onClick={() => setRegionFilter('all')}>
                  <HStack justify="space-between" w="full">
                    <Text>Alle Regionen</Text>
                    {regionFilter === 'all' && <LuCheck />}
                  </HStack>
                </Menu.Item>

                {regions.map(region => (
                  <Menu.Item
                    key={region}
                    value={`filter:vxx:${region}`}
                    onClick={() => setRegionFilter(region)}
                  >
                    <HStack justify="space-between" w="full">
                      <Text>{region}</Text>
                      {regionFilter === region && <LuCheck />}
                    </HStack>
                  </Menu.Item>
                ))}

                <Menu.Separator />

                <Box px={3} py={2}>
                  <Text fontSize="xs" color="fg.subtle" fontWeight="semibold">
                    Zeitraum
                  </Text>
                </Box>

                {(
                  [
                    { value: 'all', label: 'Alle Zeiträume' },
                    { value: 'with', label: 'Mit Zeitraum' },
                    { value: 'without', label: 'Ohne Zeitraum' },
                  ] as const
                ).map(option => (
                  <Menu.Item
                    key={option.value}
                    value={`filter:period:${option.value}`}
                    onClick={() => setPeriodFilter(option.value)}
                  >
                    <HStack justify="space-between" w="full">
                      <Text>{option.label}</Text>
                      {periodFilter === option.value && <LuCheck />}
                    </HStack>
                  </Menu.Item>
                ))}

                <Menu.Separator />

                <Box px={3} py={2}>
                  <Text fontSize="xs" color="fg.subtle" fontWeight="semibold">
                    Rückmeldungen
                  </Text>
                </Box>

                {(
                  [
                    { value: 'all', label: 'Alle' },
                    { value: 'interest', label: 'Mit Interessenten' },
                    { value: 'none', label: 'Ohne Interessenten' },
                    { value: 'rejection', label: 'Mit Ablehnungen' },
                  ] as const
                ).map(option => (
                  <Menu.Item
                    key={option.value}
                    value={`filter:responses:${option.value}`}
                    onClick={() => setResponseFilter(option.value)}
                  >
                    <HStack justify="space-between" w="full">
                      <Text>{option.label}</Text>
                      {responseFilter === option.value && <LuCheck />}
                    </HStack>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </HStack>

      <Box borderWidth="1px" borderColor="border" borderRadius="xl" overflow="hidden" bg="bg">
        <Box overflowX="auto">
          <Table.Root minW="1180px" bg="bg">
            <Table.Header bg="bg">
              <Table.Row>
                <Table.ColumnHeader
                  maxW="360px"
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderColor="border"
                  {...stickyHeaderStyles}
                >
                  Bank mit Bedarf
                </Table.ColumnHeader>

                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                >
                  <HStack gap={1} align="center">
                    <Text as="span" whiteSpace="nowrap">
                      Leistungen
                    </Text>
                    <InfoHover label="Leistungen Info">
                      <Text fontSize="sm">
                        Die Zelle zeigt den angefragten Leistungsumfang. Der Zeitraum des Bedarfs
                        steht darunter als Description.
                      </Text>
                    </InfoHover>
                  </HStack>
                </Table.ColumnHeader>

                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  <HStack gap={1} align="center">
                    <Text as="span" whiteSpace="nowrap">
                      Dauer
                    </Text>
                    <InfoHover label="Dauer Info" maxW="420px">
                      <Text fontSize="sm">
                        Hier steht nur die Dauer der Ausschreibung auf dem SIB Portal Marktplatz.
                        Der Bearbeitungszeitraum selbst steht als Unterzeile in{' '}
                        <Text as="span" fontWeight="semibold">
                          Leistungen
                        </Text>
                        .
                      </Text>
                    </InfoHover>
                  </HStack>
                </Table.ColumnHeader>

                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                >
                  <HStack gap={1} align="center">
                    <Text as="span" whiteSpace="nowrap">
                      Status
                    </Text>
                    <InfoHover label="Status Info" maxW="420px">
                      <Text fontSize="sm">
                        Die Status-Badges zeigen den aktuellen Prozessstand des Marktplatzbedarfs.
                      </Text>
                      {STATUS_LEGEND.map(([colorPalette, label, description]) => (
                        <HStack key={label} gap={2} align="start">
                          <Badge
                            colorPalette={colorPalette}
                            variant="subtle"
                            size="sm"
                            flexShrink={0}
                          >
                            {label}
                          </Badge>
                          <Text fontSize="xs" color="fg.subtle">
                            {description}
                          </Text>
                        </HStack>
                      ))}
                    </InfoHover>
                  </HStack>
                </Table.ColumnHeader>

                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  Rückmeldungen
                </Table.ColumnHeader>

                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  PLZ / Ort
                </Table.ColumnHeader>

                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  Vxx
                </Table.ColumnHeader>

                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  Sichtbarkeit
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {visibleDemands.map(demand => {
                const status = statusTone(demand)
                const visibility = visibilityTone(demand)
                const finished = isDemandFinished(demand)
                const remaining = remainingDaysLabel(demand.expiresAt)

                return (
                  <Table.Row
                    key={demand.id}
                    cursor="pointer"
                    onClick={() =>
                      navigate(`/union/demands/${demand.id}`, {
                        state: { returnToUnionMarketplace: true },
                      })
                    }
                    _hover={{ bg: 'bg.subtle' }}
                  >
                    <Table.Cell
                      {...tableCellProps}
                      maxW="360px"
                      borderTopWidth="1px"
                      borderColor="border"
                      {...stickyColStyles}
                    >
                      <Stack gap={0.5} minW={0} maxW="320px">
                        <Text
                          fontWeight="medium"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          maxW="320px"
                        >
                          {bankName(demand)}
                        </Text>
                        <Text fontSize="xs" color="fg.subtle" whiteSpace="nowrap">
                          BLZ {demand.customerId ?? demand.bankId ?? '—'}
                        </Text>
                      </Stack>
                    </Table.Cell>

                    <Table.Cell
                      {...tableCellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      whiteSpace="nowrap"
                    >
                      <Stack gap={0.5}>
                        <Text whiteSpace="nowrap">{servicesText(demand)}</Text>
                        <Text fontSize="xs" color="fg.subtle" whiteSpace="nowrap">
                          {formatPeriod(demand.periodStart, demand.periodEnd)}
                        </Text>
                      </Stack>
                    </Table.Cell>

                    <Table.Cell
                      {...tableCellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      whiteSpace="nowrap"
                    >
                      <Stack gap={0.5}>
                        <Text whiteSpace="nowrap">
                          {finished ? 'beendet' : formatDate(demand.expiresAt)}
                        </Text>
                        {!finished && remaining && (
                          <Badge colorPalette="gray" variant="plain" size="xs" w="fit-content">
                            {remaining}
                          </Badge>
                        )}
                      </Stack>
                    </Table.Cell>

                    <Table.Cell
                      {...tableCellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      whiteSpace="nowrap"
                    >
                      <Badge
                        colorPalette={status.colorPalette}
                        variant="subtle"
                        size="sm"
                        whiteSpace="nowrap"
                      >
                        {status.label}
                      </Badge>
                    </Table.Cell>

                    <Table.Cell
                      {...tableCellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      whiteSpace="nowrap"
                    >
                      <Text
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        maxW="210px"
                      >
                        {responsesText(demand)}
                      </Text>
                    </Table.Cell>

                    <Table.Cell
                      {...tableCellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      color="fg.muted"
                      whiteSpace="nowrap"
                    >
                      {demand.postal && demand.city
                        ? `${demand.postal} ${demand.city}`
                        : demand.city || '—'}
                    </Table.Cell>

                    <Table.Cell
                      {...tableCellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      color="fg.muted"
                      whiteSpace="nowrap"
                    >
                      {demand.salesRegion ?? '—'}
                    </Table.Cell>

                    <Table.Cell
                      {...tableCellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      whiteSpace="nowrap"
                    >
                      <Badge
                        colorPalette={visibility.colorPalette}
                        variant="subtle"
                        size="sm"
                        whiteSpace="nowrap"
                      >
                        {visibility.label}
                      </Badge>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>

      <CreateDemandDrawer
        open={showCreateDrawer}
        isSubmitting={isCreating}
        onOpenChange={setShowCreateDrawer}
        onSubmit={handleCreateDemand}
      />
    </VStack>
  )
}
